import { z } from 'zod';
import { imageWithUploadSchema } from './image.schema';
import { nameSchema } from '../validation';

//* bank names
export const bankNameSchema = z.enum([
  'alrajhi',
  'alahli',
  'alinma',
  'albilad',
  'arab_national',
  'riyad',
  'saudi_awwal',
  'saudi_fransi',
]);
export type BankName = z.infer<typeof bankNameSchema>;

export const ibanNumberSchema = z.coerce
  .string()
  .trim()
  .min(22)
  .transform(value => {
    /**
     * The structure of the 24 characters is as follows:
     *
     * 2-letter country code (SA).
     * 2-digit check number.
     * 2-character bank code.
     * 18-digit domestic account number.
     *
     * NOTE: By Default we will provide the first two SA letters ... that's why we require a minuim or 22
     */

    // Remove all spaces
    let cleaned = value.replace(/\s+/g, '');

    // Add "SA" prefix if not present
    if (!cleaned.toUpperCase().startsWith('SA')) {
      cleaned = 'SA' + cleaned;
    } else {
      cleaned = cleaned.toUpperCase();
    }

    return cleaned;
  })
  .refine(
    value => {
      const MAX_IBAN_LENGTH = 24;
      // Validate the final format: exactly 24 characters
      if (value.length !== MAX_IBAN_LENGTH) return false;

      // Validate structure: SA + 2 digits + 2 alphanumeric + 18 digits
      const ibanPattern = /^SA\d{2}[A-Z0-9]{2}\d{18}$/;
      return ibanPattern.test(value);
    },
    {
      message: 'IBAN must be 24 characters: SA + 2 check digits + 2 bank code + 18 account number',
    },
  );
export const bankCommonInfoSchema = z.object({
  /** Iban number that consists of 34 letters and numbers */
  IBAN: ibanNumberSchema,
  /** the bank account name such as ALTREQ ALRAQMI LLC*/
  accountName: nameSchema.min(3),
  /** the name of the bank such as AlRajhi, Alinma, etc */
  bankName: bankNameSchema,
  /** an image that proves the account info (name, Iban) */
  accountImage: imageWithUploadSchema,
});
export type BankCommonInfo = z.infer<typeof bankCommonInfoSchema>;
